import posthog from 'posthog-js';
import { useCallback, useEffect } from 'react';

import { useDialog } from '../../hooks/useDialog';
import { useStore } from '../../hooks/useStore';
import useTranslation from '../../hooks/useTranslation';
import useCurrentCurrency from '../../hooks/useCurrentCurrency';

import { formatPrice } from '@/lib/utils/currency';

import SamsungTradeIn from '../../components/icons/SamsungTradeIn';
import TradeInCompleted from '../../components/icons/TradeInCompleted';
import BannerImage from './components/BannerImage';
import { useNavigator } from '../../hooks/useNavigator';

const StartBanner = () => {
  const setShowDialog = useDialog((state) => state.setShowDialog);
  const currency = useCurrentCurrency();
  const campaign = useStore((state) => state.campaign);

  const onClick = useCallback(() => {
    setShowDialog(true);
  }, [setShowDialog]);

  useEffect(() => {
    posthog.capture('Show Start Banner');
  }, []);

  return (
    <section className="@container/banner text-left overflow-hidden banner">
      <div className="p-4 @[484px]/banner:p-6">
        <div className="flex justify-between items-star">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-2 mb-2">
              <SamsungTradeIn className="w-6 h-6 stroke-[#1f1f1f] @[484px]/banner:hidden" />
              <p className="text-lg @[484px]/banner:text-xl leading-[22px] @[484px]/banner:leading-6 font-medium">
                Byt in din pryl!
              </p>
            </div>
            <p className="text-base font-normal leading-6 mb-6">
              Lämna in din gamla mobil, surfplatta eller smartklocka och få
              pengar tillbaka när du köper en ny Samsungmobil.
            </p>
          </div>
          <BannerImage className="flex-none h-28 w-28 pl-2 rounded-xl object-cover hidden @[484px]/banner:block" />
        </div>
        {Boolean(campaign?.amount) && (
          <p className="self-stretch text-base text-[#004496] font-normal leading-6 border-t border-[#004496] bg-[#e4f2ff] mb-6 p-4">
            Just nu!{' '}
            <strong className="font-semibold">
              Minst {formatPrice(campaign?.amount, currency)}
            </strong>{' '}
            tillbaka när du byter in och köper en ny Samsung Galaxy S25 / S25
            Plus / S25 Ultra
          </p>
        )}
        <button
          aria-label="Byt in din gamla enhet och se vad den ar värd"
          className={`inline-flex items-center text-center border cursor-pointer bannerButton`}
          onClick={onClick}
        >
          Värdera min pryl
        </button>
      </div>
    </section>
  );
};

const CompletedBanner = () => {
  const translations = useTranslation();
  const tradeIn = useStore((state) => state.tradeIn);
  const setShowDialog = useDialog((state) => state.setShowDialog);
  const resetNavigation = useNavigator((state) => state.reset);

  const onClick = useCallback(() => {
    resetNavigation();
    setShowDialog(true);
  }, [setShowDialog, resetNavigation]);

  if (!tradeIn) return null;

  useEffect(() => {
    posthog.capture('Show Completed Banner', {
      amount: tradeIn.amount,
      currency: tradeIn.currency,
      small: false,
    });
  }, []);

  return (
    <section className="@container/banner text-left overflow-hidden banner">
      <div className="p-4 @[484px]/banner:p-6">
        <div className="flex items-center gap-2 mb-2">
          <SamsungTradeIn className="w-6 h-6 stroke-[#1f1f1f]" />
          <p className="flex flex-row w-full justify-between items-center text-lg @[484px]/banner:text-xl leading-[22px] @[484px]/banner:leading-6 font-medium">
            {translations['completedTitle']}
            <span
              className="text-sm font-normal underline align-middle cursor-pointer whitespace-nowrap"
              onClick={onClick}
            >
              {translations['completedRedo']}
            </span>
          </p>
        </div>
        <p className="text-base font-normal leading-6 mb-2">
          {tradeIn.model?.ProductName}
        </p>
        <div className="flex self-stretch text-base border-t border-[#005112] bg-[#e7f4e5] mb-4 p-4">
          <TradeInCompleted className="w-6 h-6 flex-none stroke-[#005112]" />
          <p className="pl-3 text-[#005112] font-normal leading-6">
            Efter avslutat köp får du ett mail med vidare instruktioner.
          </p>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <p className="text-lg font-medium">Du får tillbaka</p>
          <p className="text-2xl @[484px]/banner:text-[32px] leading-7 @[484px]/banner:leading-9 font-medium text-[#630132]">
            {formatPrice(tradeIn.amount)}
            <span className="text-sm leading-5 font-normal"> kr</span>
          </p>
          <p className="text-xs @[484px]/banner:text-sm">
            Kan ändras efter slutgiltig värdering
          </p>
        </div>
      </div>
    </section>
  );
};

const Banner = () => {
  const tradeIn = useStore((state) => state.tradeIn);

  return tradeIn ? <CompletedBanner /> : <StartBanner />;
};

export default Banner;
